import { SITE_CONFIG } from '@vastra/constants';

export const DEFAULT_TITLE =
  'Style with Vikashini — Buy Pure Silk Sarees Online | Handloom & Bridal Collection';
export const DEFAULT_DESCRIPTION =
  'Shop authentic Indian sarees online at Style with Vikashini. Curated handloom Pure Silk, Kanchipuram, Banarasi, Soft Silk, Chanderi, Tussar & Cotton sarees for weddings and festive occasions. Fast dispatch & pan-India delivery.';
export const DEFAULT_KEYWORDS =
  'Style with Vikashini, buy sarees online, pure silk sarees, kanchipuram silk sarees, banarasi sarees, soft silk sarees, handloom sarees, wedding sarees, bridal saree collection, traditional indian sarees, cotton sarees, chanderi sarees, tussar silk sarees, festive sarees, authentic indian textiles, sarees online shopping india, vikashini sarees';

const JSON_LD_ATTR = 'data-vastra-jsonld';

function setOrCreateMeta(nameOrProp: 'name' | 'property', key: string, content: string) {
  const sel = `meta[${nameOrProp}="${CSS.escape(key)}"]`;
  let el = document.querySelector(sel) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(nameOrProp, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(url: string) {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  link.href = url;
}

export function stripHtml(s: string): string {
  return s.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

export function getStoreOrigin(): string {
  const storeUrl =
    typeof process !== 'undefined' && process.env && process.env.REACT_APP_STORE_URL
      ? String(process.env.REACT_APP_STORE_URL)
      : '';
  const origin = (storeUrl || (typeof window !== 'undefined' ? window.location.origin : '')).replace(/\/$/, '');
  return origin;
}

export function absoluteUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const origin = getStoreOrigin();
  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${origin}${path}`;
}

export function defaultOgImageUrl(): string {
  return absoluteUrl('/android-chrome-512x512.png');
}

export type PageSeoOptions = {
  title: string;
  description: string;
  /** Path only (e.g. `/products`) or absolute URL. Used for canonical + og:url. */
  path: string;
  imageUrl?: string;
  /** Open Graph type. Defaults to `website`. */
  type?: string;
  /** robots meta content, e.g. `noindex, nofollow`. Omit for default indexable. */
  robots?: string;
  /** Targeted search keywords for this page */
  keywords?: string;
  /** Extra Open Graph properties */
  ogExtra?: Record<string, string>;
};

export function setPageSeo(opts: PageSeoOptions): void {
  const canonical = absoluteUrl(opts.path);
  const image = opts.imageUrl ? absoluteUrl(opts.imageUrl) : defaultOgImageUrl();
  const type = opts.type ?? 'website';
  const desc = stripHtml(opts.description).slice(0, 160) || DEFAULT_DESCRIPTION;
  const keywords = opts.keywords || DEFAULT_KEYWORDS;

  document.title = opts.title;
  setCanonical(canonical);
  setOrCreateMeta('name', 'description', desc);
  setOrCreateMeta('name', 'keywords', keywords);
  setOrCreateMeta('name', 'robots', opts.robots || 'index, follow, max-image-preview:large');

  // Open Graph
  setOrCreateMeta('property', 'og:site_name', SITE_CONFIG.siteName);
  setOrCreateMeta('property', 'og:locale', 'en_IN');
  setOrCreateMeta('property', 'og:title', opts.title);
  setOrCreateMeta('property', 'og:description', desc);
  setOrCreateMeta('property', 'og:type', type);
  setOrCreateMeta('property', 'og:url', canonical);
  setOrCreateMeta('property', 'og:image', image);
  setOrCreateMeta('property', 'og:image:alt', opts.title);

  // Extra OG tags (e.g. product price)
  if (opts.ogExtra) {
    for (const [key, val] of Object.entries(opts.ogExtra)) {
      if (val) setOrCreateMeta('property', key, val);
    }
  }

  // Twitter Card
  setOrCreateMeta('name', 'twitter:card', 'summary_large_image');
  setOrCreateMeta('name', 'twitter:title', opts.title);
  setOrCreateMeta('name', 'twitter:description', desc);
  setOrCreateMeta('name', 'twitter:image', image);
  setOrCreateMeta('name', 'twitter:image:alt', opts.title);
}

/** Restore shell defaults and remove page-scoped JSON-LD. */
export function clearPageSeo(): void {
  document.title = DEFAULT_TITLE;
  setOrCreateMeta('name', 'description', DEFAULT_DESCRIPTION);
  setOrCreateMeta('name', 'keywords', DEFAULT_KEYWORDS);
  setOrCreateMeta('name', 'robots', 'index, follow, max-image-preview:large');
  setOrCreateMeta('property', 'og:site_name', SITE_CONFIG.siteName);
  setOrCreateMeta('property', 'og:locale', 'en_IN');
  setOrCreateMeta('property', 'og:title', DEFAULT_TITLE);
  setOrCreateMeta('property', 'og:description', DEFAULT_DESCRIPTION);
  setOrCreateMeta('property', 'og:type', 'website');
  setOrCreateMeta('property', 'og:url', absoluteUrl('/'));
  setOrCreateMeta('property', 'og:image', defaultOgImageUrl());
  setOrCreateMeta('name', 'twitter:card', 'summary_large_image');
  setOrCreateMeta('name', 'twitter:title', DEFAULT_TITLE);
  setOrCreateMeta('name', 'twitter:description', DEFAULT_DESCRIPTION);
  setOrCreateMeta('name', 'twitter:image', defaultOgImageUrl());
  setCanonical(absoluteUrl('/'));
  removeAllJsonLd();
}

export function setJsonLd(id: string, data: Record<string, unknown> | Record<string, unknown>[]): void {
  const scriptId = `vastra-jsonld-${id}`;
  let el = document.getElementById(scriptId) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = scriptId;
    el.setAttribute(JSON_LD_ATTR, id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function removeJsonLd(id: string): void {
  document.getElementById(`vastra-jsonld-${id}`)?.remove();
}

export function removeAllJsonLd(): void {
  document.querySelectorAll(`script[${JSON_LD_ATTR}]`).forEach((el) => el.remove());
}

export function setProductSeo(opts: {
  productName: string;
  descriptionHtml?: string;
  imageUrl?: string;
  images?: string[];
  canonicalPath: string;
  price?: number;
  compareAtPrice?: number;
  currency?: string;
  availability?: 'InStock' | 'OutOfStock' | 'PreOrder';
  sku?: string;
  category?: string;
  color?: string;
  material?: string;
  occasion?: string;
  rating?: number;
  reviewCount?: number;
  reviews?: { author: string; rating: number; comment?: string; date?: string }[];
}): void {
  const title = `${opts.productName} — Buy Pure Handloom Saree | ${SITE_CONFIG.siteName}`;
  const rawDesc = opts.descriptionHtml
    ? stripHtml(opts.descriptionHtml)
    : `${opts.productName} handcrafted traditional saree from Style with Vikashini. Features authentic weave, rich texture, and detailed styling guidance.`;
  const desc = rawDesc.slice(0, 160);
  const path = opts.canonicalPath.startsWith('/') ? opts.canonicalPath : `/${opts.canonicalPath}`;

  const ogExtra: Record<string, string> = {};
  if (typeof opts.price === 'number') {
    ogExtra['product:price:amount'] = String(opts.price);
    ogExtra['product:price:currency'] = opts.currency || 'INR';
    ogExtra['product:availability'] = opts.availability === 'OutOfStock' ? 'out of stock' : 'in stock';
  }

  setPageSeo({
    title,
    description: desc,
    path,
    imageUrl: opts.imageUrl,
    type: 'product',
    keywords: `${opts.productName}, ${opts.category || 'sarees'}, ${opts.material || 'silk'}, ${opts.color || ''}, buy ${opts.productName} online, ${SITE_CONFIG.siteName}`,
    ogExtra,
  });

  const allImages = opts.images?.length
    ? opts.images.map((img) => absoluteUrl(img))
    : opts.imageUrl
      ? [absoluteUrl(opts.imageUrl)]
      : [defaultOgImageUrl()];

  const productLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: opts.productName,
    description: rawDesc,
    image: allImages,
    url: absoluteUrl(path),
    sku: opts.sku || opts.canonicalPath.replace('/product/', ''),
    mpn: opts.sku || opts.canonicalPath.replace('/product/', ''),
    brand: {
      '@type': 'Brand',
      name: SITE_CONFIG.siteName,
    },
    category: opts.category || 'Traditional Sarees',
    color: opts.color,
    material: opts.material,
    itemCondition: 'https://schema.org/NewCondition',
  };

  if (typeof opts.price === 'number' && Number.isFinite(opts.price)) {
    productLd.offers = {
      '@type': 'Offer',
      priceCurrency: opts.currency || SITE_CONFIG.currency || 'INR',
      price: opts.price,
      availability: `https://schema.org/${opts.availability ?? 'InStock'}`,
      itemCondition: 'https://schema.org/NewCondition',
      priceValidUntil: '2027-12-31',
      url: absoluteUrl(path),
      seller: {
        '@type': 'Organization',
        name: SITE_CONFIG.siteName,
      },
      hasMerchantReturnPolicy: {
        '@type': 'MerchantReturnPolicy',
        applicableCountry: 'IN',
        returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
        merchantReturnDays: 7,
        returnMethod: 'https://schema.org/ReturnByMail',
        returnFees: 'https://schema.org/ReturnFeesCustomerResponsibility',
      },
      shippingDetails: {
        '@type': 'OfferShippingDetails',
        shippingRate: {
          '@type': 'MonetaryAmount',
          value: 0,
          currency: 'INR',
        },
        shippingDestination: {
          '@type': 'DefinedRegion',
          addressCountry: 'IN',
        },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          transitTime: {
            '@type': 'QuantitativeValue',
            minValue: 3,
            maxValue: 7,
            unitCode: 'DAY',
          },
        },
      },
    };
  }

  if (opts.rating && opts.reviewCount && opts.reviewCount > 0) {
    productLd.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: opts.rating,
      reviewCount: opts.reviewCount,
      bestRating: 5,
      worstRating: 1,
    };
  }

  if (opts.reviews && opts.reviews.length > 0) {
    productLd.review = opts.reviews.map((r) => ({
      '@type': 'Review',
      author: { '@type': 'Person', name: r.author || 'Verified Customer' },
      reviewRating: {
        '@type': 'Rating',
        ratingValue: r.rating,
        bestRating: 5,
        worstRating: 1,
      },
      reviewBody: r.comment || '',
      datePublished: r.date || new Date().toISOString().split('T')[0],
    }));
  }

  setJsonLd('product', productLd);

  // Breadcrumbs: Home > Sarees > [Category] > Product
  const breadcrumbItems = [
    { '@type': 'ListItem', position: 1, name: 'Home', item: absoluteUrl('/') },
    { '@type': 'ListItem', position: 2, name: 'Sarees', item: absoluteUrl('/products') },
  ];
  if (opts.category) {
    breadcrumbItems.push({
      '@type': 'ListItem',
      position: 3,
      name: opts.category,
      item: absoluteUrl(`/products?category=${encodeURIComponent(opts.category)}`),
    });
    breadcrumbItems.push({
      '@type': 'ListItem',
      position: 4,
      name: opts.productName,
      item: absoluteUrl(path),
    });
  } else {
    breadcrumbItems.push({
      '@type': 'ListItem',
      position: 3,
      name: opts.productName,
      item: absoluteUrl(path),
    });
  }

  setJsonLd('breadcrumb', {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems,
  });
}

export function setItemListJsonLd(
  listName: string,
  items: { name: string; path: string; imageUrl?: string; price?: number }[],
): void {
  if (!items.length) {
    removeJsonLd('itemlist');
    return;
  }
  setJsonLd('itemlist', {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: listName,
    numberOfItems: items.length,
    itemListElement: items.slice(0, 16).map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Product',
        name: item.name,
        url: absoluteUrl(item.path),
        image: item.imageUrl ? [absoluteUrl(item.imageUrl)] : undefined,
        offers: item.price
          ? {
              '@type': 'Offer',
              price: item.price,
              priceCurrency: 'INR',
              availability: 'https://schema.org/InStock',
            }
          : undefined,
      },
    })),
  });
}

export function setBreadcrumbJsonLd(crumbs: { name: string; path: string }[]): void {
  if (!crumbs.length) return;
  setJsonLd('breadcrumb', {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: crumb.name,
      item: absoluteUrl(crumb.path),
    })),
  });
}

export function setFaqJsonLd(faqs: { question: string; answer: string }[]): void {
  if (!faqs.length) return;
  setJsonLd('faq', {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.answer,
      },
    })),
  });
}

export function setOrganizationJsonLd(): void {
  const origin = getStoreOrigin();
  setJsonLd('organization', {
    '@context': 'https://schema.org',
    '@type': ['Organization', 'ClothingStore', 'Store', 'OnlineStore'],
    name: SITE_CONFIG.siteName,
    alternateName: 'SWV Sarees',
    url: origin,
    logo: absoluteUrl('/android-chrome-512x512.png'),
    image: [absoluteUrl('/android-chrome-512x512.png'), absoluteUrl('/logo.jpg')],
    description: DEFAULT_DESCRIPTION,
    email: SITE_CONFIG.contactEmail,
    telephone: SITE_CONFIG.contactPhone,
    priceRange: '₹₹',
    currenciesAccepted: 'INR',
    paymentAccepted: 'UPI, Credit Card, Debit Card, Net Banking',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'IN',
    },
    sameAs: Object.values(SITE_CONFIG.socialLinks || {}).filter(Boolean),
    hasMerchantReturnPolicy: {
      '@type': 'MerchantReturnPolicy',
      applicableCountry: 'IN',
      returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
      merchantReturnDays: 7,
      returnMethod: 'https://schema.org/ReturnByMail',
      returnFees: 'https://schema.org/ReturnFeesCustomerResponsibility',
    },
  });

  setJsonLd('website', {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_CONFIG.siteName,
    alternateName: 'Style with Vikashini Sarees',
    url: origin,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${origin}/products?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  });
}
